/* eslint-disable react/jsx-no-bind */
import classNames from "classnames";
import Badge from "app/components/shared/Badge";
import { NavLink, NavLinkProps, useMatches } from "react-router-dom";
import { useBuild } from "app/components/build/Show/lib/BuildContext";
import { BuildView } from "..";
import { urlForView } from "../lib/urlForView";
import useQueryParams from "../lib/useQueryParams";
import { useMobileBreakpoint } from "../lib/useBreakpoints";
import { useMemo } from "react";

interface BuildNavLinkProps extends NavLinkProps {
  isActive?: boolean;
  children: React.ReactNode;
}

const BuildNavLink = ({
  isActive: forcedActive,
  ...props
}: BuildNavLinkProps) => {
  return (
    <NavLink
      {...props}
      className={({ isActive }) =>
        classNames(
          "px-2 text-sm",
          "btn btn-tab text-current focus:text-current transition-colors",
          {
            "btn-tab--active": forcedActive ?? isActive,
          },
        )
      }
    />
  );
};

// Helper to get the base view from nested routes
function getBaseView(): BuildView {
  const matches = useMatches();
  // Reverse match to find the first BuildView parent
  const viewMatch = [...matches]
    .reverse()
    .find((match) => Object.values(BuildView).includes(match.id as BuildView));
  return (viewMatch?.id as BuildView) || BuildView.Table;
}

function BuildContentNavigation() {
  const { store, build } = useBuild();
  const params = useQueryParams();
  const isMobile = useMobileBreakpoint();

  if (!store || !build) {
    throw new Error("Missing build context");
  }

  // Assemble the URLs for the different views for this build;
  const summaryHref = urlForView(build.path, BuildView.Summary, params);
  const testDigestHref = urlForView(build.path, BuildView.TestDigest, params);
  const testsHref = urlForView(build.path, BuildView.Tests, params);
  const canvasHref = urlForView(build.path, BuildView.Canvas, params);
  const waterfallHref = urlForView(build.path, BuildView.Waterfall, params);

  // Get the base view regardless of step selection
  const baseView = getBaseView();

  // For non-sidebar mode, use the base path for Jobs tab
  const listHref = Features.BuildSidebar
    ? urlForView(build.path, BuildView.Table, params)
    : build.path;

  const showTestDigest = build.hasTestAnalytics;
  const showBuildTests = Features.TestEngineBuildPageUplift;

  if (Features.BuildSidebar) {
    // Determine effective view for mobile
    const effectiveView =
      isMobile &&
      [BuildView.Canvas, BuildView.Waterfall, BuildView.TestDigest].includes(
        baseView,
      )
        ? BuildView.Table
        : baseView;

    // Build navigation items based on the effective view
    const navItems = useMemo(() => {
      if (isMobile) {
        return [
          {
            to: summaryHref,
            label: "Summary",
            view: BuildView.Summary,
          },
          {
            to: listHref,
            label: "Jobs",
            view: BuildView.Table,
          },
        ];
      }

      const items = [
        { to: summaryHref, label: "Summary", view: BuildView.Summary },
        { to: canvasHref, label: "Canvas", view: BuildView.Canvas },
        { to: listHref, label: "Table", view: BuildView.Table },
        {
          to: waterfallHref,
          label: "Waterfall",
          view: BuildView.Waterfall,
          badge: !store.waterfallAvailable && (
            <Badge outline={false} className="white bg-purple">
              Upgrade
            </Badge>
          ),
        },
      ];

      if (showTestDigest) {
        items.push({
          to: testDigestHref,
          label: "Test digest",
          view: BuildView.TestDigest,
        });

        if (showBuildTests) {
          items.push({
            to: testsHref,
            label: "Tests",
            view: BuildView.Tests,
          });
        }
      }

      return items;
    }, [
      isMobile,
      summaryHref,
      listHref,
      canvasHref,
      waterfallHref,
      testDigestHref,
      testsHref,
      store.waterfallAvailable,
      showTestDigest,
    ]);

    return (
      <nav data-testid="BuildContentNavigation" className="flex">
        {navItems.map(
          (item: {
            to: string;
            label: string;
            view: BuildView;
            badge?: React.ReactNode;
          }) => (
            <BuildNavLink
              key={item.to}
              to={item.to}
              isActive={effectiveView === item.view}
            >
              <span>{item.label}</span>
              {item.badge}
            </BuildNavLink>
          ),
        )}
      </nav>
    );
  }

  // Navigation for users without BuildSidebar feature
  return (
    <nav
      className="Tablist flex border-b border-gray mb-[-1px]"
      data-testid="buildViewTabsNav"
    >
      <NavLink
        to={listHref}
        end={true}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Jobs</span>
      </NavLink>

      <NavLink
        to={canvasHref}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Canvas</span>
      </NavLink>

      <NavLink
        to={waterfallHref}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Waterfall</span>
        {!store.waterfallAvailable && (
          <Badge outline={false} className="white bg-purple">
            Upgrade
          </Badge>
        )}
      </NavLink>

      {showTestDigest && (
        <>
          <NavLink
            to={testDigestHref}
            className={({ isActive }) =>
              classNames("btn btn-tab text-current focus:text-current", {
                "btn-tab--active": isActive,
              })
            }
          >
            <span>Test digest</span>
          </NavLink>
          {showBuildTests && (
            <NavLink
              to={testsHref}
              className={({ isActive }) =>
                classNames("btn btn-tab text-current focus:text-current", {
                  "btn-tab--active": isActive,
                })
              }
            >
              <span>Tests</span>
            </NavLink>
          )}
        </>
      )}
    </nav>
  );
}

export default BuildContentNavigation;
